/**
 * Pure orbital mechanics for the satellite detail view. Derives period, speed,
 * and apogee/perigee from the real mean elements (mean motion + eccentricity)
 * propagated from TLEs; falls back to an altitude-only estimate when an element
 * is missing. No regex; closed-form math only.
 */

import { groundRadiusKm } from '@/map/coverage/footprint-geometry';

/** Mean Earth radius (km). */
const EARTH_RADIUS_KM = 6371;

/** Earth's gravitational parameter (km³/s²). */
const GM_EARTH = 398_600.4418;

/** Seconds and minutes per day. */
const SECONDS_PER_DAY = 86_400;
const MINUTES_PER_DAY = 1_440;

/** Derived orbital parameters for display. */
export interface OrbitParams {
  semiMajorAxisKm: number;
  periodMinutes: number;
  speedKmps: number;
  apogeeKm: number;
  perigeeKm: number;
  footprintRadiusKm: number;
}

/** Compute orbital parameters from altitude (km), mean motion (rev/day), and eccentricity. */
export function computeOrbit(altitudeKm: number, meanMotion: number, eccentricity: number): OrbitParams {
  const a = semiMajorAxisKm(altitudeKm, meanMotion);
  return {
    semiMajorAxisKm: a,
    periodMinutes: meanMotion > 0 ? MINUTES_PER_DAY / meanMotion : periodFromAxis(a),
    speedKmps: Math.sqrt(GM_EARTH / a),
    apogeeKm: a * (1 + eccentricity) - EARTH_RADIUS_KM,
    perigeeKm: a * (1 - eccentricity) - EARTH_RADIUS_KM,
    footprintRadiusKm: groundRadiusKm(altitudeKm),
  };
}

/** Semi-major axis (km): from mean motion via Kepler's third law, else altitude + R. */
function semiMajorAxisKm(altitudeKm: number, meanMotion: number): number {
  if (meanMotion <= 0) return altitudeKm + EARTH_RADIUS_KM;
  const periodSeconds = SECONDS_PER_DAY / meanMotion;
  return Math.cbrt((GM_EARTH * periodSeconds * periodSeconds) / (4 * Math.PI * Math.PI));
}

/** Orbital period (min) from the semi-major axis (fallback when mean motion is absent). */
function periodFromAxis(semiMajorAxisKm: number): number {
  return (2 * Math.PI * Math.sqrt(semiMajorAxisKm ** 3 / GM_EARTH)) / 60;
}
